import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  SparkIteration,
  SparkFeedbackItem,
  SparkCreativeType,
  SparkFlowStep,
  SparkFormDefinition,
  SparkAttachment,
} from '../../types/sparkTypes';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import styles from '../../styles/IterationView.module.css';
import editorStyles from '../../styles/CreativeEditor.module.css';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import TextareaAutosize from 'react-textarea-autosize';
import { ConfirmationModal } from '../modals/ConfirmationModal';
import useSparkProjectData from '../../hooks/useSparkProjectData';
import { useSparkActions, ResolvedSparkAction } from '../../hooks/useSparkActions';
import SparkActionModal from '../modals/SparkActionModal';
import { SparkCreative } from '../../types/sparkTypes';
import { isIterationUnseen, parseTimestamp } from '../../utils/timeUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCitation } from '../../../../hooks/useCitation';
import { normalizeMarkdownBullets } from '../../utils/markdown';
import { useTypewriter } from '../../../../hooks/useTypewriter';
import { useAuth } from '../../../../contexts/AuthContext';

// MUI Icons
import LabelIcon from '@mui/icons-material/Label';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SendIcon from '@mui/icons-material/Send';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import CircularProgress from '@mui/material/CircularProgress';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Import the new custom icon
import { DeleteIcon } from '../icons/DeleteIcon';
import { WordFileIcon, ExcelFileIcon, PowerPointFileIcon } from '../../../../components/icons/OfficeFileIcons';

// Import atlasClient
import { atlasClient } from '../../../../api/atlasClient';
import { formatBytes } from '../../../../utils/format';
import { authenticatedFetch, parseJsonResponse } from '../../../../api/apiClient'; // Unified fetch wrapper
import { useIterationSSE } from '../../hooks/useIterationSSE';
import { ThinkingStep } from '../../types/thinkingTypes';
import { ThinkingPanel } from '../shared/ThinkingPanel';
import { SlideLightbox } from '../modals/SlideLightbox';
import FormEditor from './FormEditor';
import { EvaluationFeedbackCard } from './EvaluationFeedbackCard';
import { formatSparkFormLabel, resolveSparkFormLabelFromOverlay } from '../../utils/forms';

interface IterationViewProps {
  iteration: SparkIteration;
  projectFqn: string;
  creativeType?: SparkCreativeType;
  creativeTypeName?: string;
  creativeLabels?: string[];
  activeJobsCount: number;
  successJobsCount: number;
  failedJobsCount: number;
  onRemoveLabel?: (label: string) => void;
  onUpdate?: () => void;
  isActiveIteration?: boolean;
  iterationNumber?: number;
  /** Indicates that a new iteration has been detected and is ready to view */
  newIterationAvailable?: boolean;
  /** Callback to load and display the new iteration */
  onLoadNewIteration?: () => void;
  /** Callback to close the parent editor/modal */
  onClose?: () => void;
  /** Indicates if there is a next unseen creative */
  hasNextUnseen?: boolean;
  /** Callback to navigate to the next unseen creative */
  onNextUnseen?: () => void;

  // --- New Props for Iteration Dropdown ---
  iterations: SparkIteration[];
  activeIterationFqn: string | null;
  displayedIterationFqn: string | null;
  handleDisplayIteration: (fqn: string) => void;
  handleSetActiveIteration: (fqn: string) => Promise<void>;
  handleCreateIteration: (fqn: string) => Promise<void>;
  handleDeleteIterationRequest: (fqn: string) => void;
  isDeletingIteration?: boolean;
  isProjectClosed?: boolean;
  isPublished?: boolean;
  isPublishing?: boolean;
  canPublish?: boolean;
  canUnpublish?: boolean;
  onPublishRequest?: () => void;
  onUnpublishRequest?: () => void;
  publishDisabledReason?: string;

  // Item mode (optional, non-breaking)
  editorMode?: 'iteration' | 'item';
  formsMap?: Record<string, SparkFormDefinition>;
  orderedFormIds?: string[];
  persistValues?: (values: Record<string, any>) => Promise<SparkIteration>;
  canDelete?: boolean;
  onDeleteRequest?: () => void;

  // Controlled tab state (optional - for parent to control active tab)
  controlledActiveTab?: 'details' | 'attachments' | 'deepsights';
  scrollToFeedbackTimestamp?: string | null;
  scrollToFeedbackId?: string | null;
  onScrollComplete?: () => void;
  onExitDeepsights?: () => void;
  /** Parent scroll container ref — when provided, deepsights chat flows in
   *  the parent scroller instead of maintaining its own overflow viewport. */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  chatSheetHostRef?: React.RefObject<HTMLDivElement | null>;
  // Attachments backend override (for Library Items mode)
  attachmentsBackend?: {
    list: () => Promise<SparkAttachment[]>;
    upload: (file: File) => Promise<SparkAttachment>;
    remove: (fileId: string) => Promise<void>;
    getDownloadUrl: (fileId: string) => string;
  };

  // New layout props (when parent controls header, tabs, sidebar)
  hideHeader?: boolean;
  controlledSelectedFormId?: string;
  onSelectedFormIdChange?: (formId: string) => void;
  // Scroll-spy section refs (populated by IterationView, observed by parent)
  sectionRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;

  // Presentation state lifting (when hideHeader is true and parent manages header)
  presentHandlersRef?: React.MutableRefObject<{
    togglePresent: () => void;
    downloadPptx: () => void;
  } | null>;
  onPresentStateChange?: (state: {
    showPresent: boolean;
    isPresentActive: boolean;
    isGeneratingSlides: boolean;
    slideCountdown: number | null;
    showDownloadPptx: boolean;
    isDownloadingPptx: boolean;
  }) => void;
}

/**
 * TypewriterFeedbackContent - Renders feedback text with typewriter animation
 */
interface TypewriterFeedbackContentProps {
  content: string;
  isAnimated: boolean;
  onAnimationComplete: () => void;
  onTyping?: () => void;
  markdownComponents: any;
  markdownPlugins: any[];
}

const TypewriterFeedbackContent: React.FC<TypewriterFeedbackContentProps> = ({
  content,
  isAnimated,
  onAnimationComplete,
  onTyping,
  markdownComponents,
  markdownPlugins,
}) => {
  const { displayedText, isTyping } = useTypewriter(
    isAnimated ? '' : content,
    {
      speed: 200,
      byWord: true,
      onComplete: onAnimationComplete,
    }
  );

  // Notify parent when typing progresses (for auto-scroll)
  useEffect(() => {
    if (isTyping && onTyping) {
      onTyping();
    }
  }, [displayedText, isTyping, onTyping]);

  const textToShow = isAnimated ? content : displayedText;

  return (
    <ReactMarkdown
      components={markdownComponents}
      remarkPlugins={markdownPlugins}
    >
      {normalizeMarkdownBullets(textToShow)}
    </ReactMarkdown>
  );
};

const DEEPSIGHTS_SHEET_TRANSITION_MS = 760;

export const IterationView: React.FC<IterationViewProps> = ({
  iteration,
  projectFqn,
  creativeType,
  // creativeTypeName,
  creativeLabels = [],
  activeJobsCount,
  successJobsCount,
  failedJobsCount,
  onRemoveLabel,
  onUpdate,
  isActiveIteration,
  iterationNumber,
  newIterationAvailable,
  onLoadNewIteration,
  onClose,
  hasNextUnseen,
  onNextUnseen,

  // --- Destructure New Props ---
  iterations,
  activeIterationFqn,
  displayedIterationFqn,
  handleDisplayIteration,
  handleSetActiveIteration,
  handleCreateIteration,
  handleDeleteIterationRequest,
  isDeletingIteration,
  isProjectClosed,
  isPublished = false,
  isPublishing = false,
  canPublish = false,
  canUnpublish = false,
  onPublishRequest,
  onUnpublishRequest,
  publishDisabledReason,
  // Item mode props
  editorMode = 'iteration',
  formsMap: formsMapOverride,
  orderedFormIds: orderedFormIdsOverride,
  persistValues,
  canDelete,
  onDeleteRequest,
  // Controlled tab state props
  controlledActiveTab,
  scrollToFeedbackTimestamp,
  scrollToFeedbackId,
  onScrollComplete,
  onExitDeepsights,
  scrollContainerRef,
  chatSheetHostRef,
  // Attachments backend
  attachmentsBackend,
  // New layout props
  hideHeader,
  controlledSelectedFormId,
  onSelectedFormIdChange,
  sectionRefs,
  // Presentation state lifting
  presentHandlersRef,
  onPresentStateChange,
}) => {
  const isItemMode = editorMode === 'item';
  const attachmentContextFqn = isItemMode ? iteration.fqn : iteration._creative_fqn;
  const closed = !!isProjectClosed;
  const isReadOnly = closed || isPublished;

  const handlePublishToggle = () => {
    if (publishActionDisabled) {
      return;
    }
    if (isPublished) {
      onUnpublishRequest?.();
    } else {
      if (!hasMissingRequiredAcrossForms) onPublishRequest?.();
    }
  };

  const informReadOnly = useCallback(() => {
    toast.info(isPublished ? 'Creative is published' : 'Project is closed');
  }, [isPublished]);

  // Media panel state inside form editor

  // Navigation for citation drill-down
  const citationNavigate = useNavigate();
  const citationLocation = useLocation();
  const onNavigateToArticle = useCallback((fqn: string, initialChapter?: string) => {
    citationNavigate(`/viewer/${encodeURIComponent(fqn)}`, {
      state: {
        initialChapter,
        returnTo: citationLocation.pathname + citationLocation.search,
        returnState: {
          initialCreativeFqn: iteration?._creative_fqn,
          initialIterationFqn: iteration?.fqn,
          initialTab: controlledActiveTab ?? 'details',
        },
      }
    });
  }, [citationNavigate, citationLocation, controlledActiveTab, iteration?._creative_fqn, iteration?.fqn]);

  // Citation Hook
  const { markdownComponents, markdownPlugins, CitationPortal, refreshCitationIndex } = useCitation(
    editorMode === 'item'
      ? { projectFqn: undefined, stripBracketRefs: true }
      : { projectFqn, onNavigateToArticle }
  );

  // Auth Hook for current user
  const { user: currentUser } = useAuth();

  // Title/description editing handled by parent header (CreativeEditor)

  // Determine if this iteration has unseen changes
  // Determine if the *displayed* iteration has unseen changes (for the badge)
  const [locallySeenAt, setLocallySeenAt] = useState<number | null>(null);
  const displayedIterationHasUnseenChanges = useMemo(() => {
    const displayed = iterations.find(it => it.fqn === displayedIterationFqn);
    if (!displayed) return false;
    const lastSeenBase = parseTimestamp(displayed._last_time_seen || null);
    const effectiveLastSeen = Math.max(lastSeenBase, locallySeenAt ?? 0);
    const changedAt = parseTimestamp(displayed._changed_at || null);
    if (!displayed._last_time_seen && locallySeenAt == null) {
      return true;
    }
    return changedAt > effectiveLastSeen;
  }, [iterations, displayedIterationFqn, locallySeenAt]);

  // State
  // Track which AI feedback items have completed their typewriter animation
  const [animatedFeedbackIds, setAnimatedFeedbackIds] = useState<Set<string>>(new Set());
  const [newLabelInput, setNewLabelInput] = useState('');
  // Local dirty state removed; we rely on store-level blocking refresh
  const activeTab = controlledActiveTab ?? 'details';
  const isDeepsightsOpen = !isItemMode && activeTab === 'deepsights';
  const [isDeepsightsExpanded, setIsDeepsightsExpanded] = useState(false);
  const [isDeepsightsSheetMounted, setIsDeepsightsSheetMounted] = useState(isDeepsightsOpen);
  const [isDeepsightsSheetVisible, setIsDeepsightsSheetVisible] = useState(isDeepsightsOpen);
  const deepsightsCloseTimerRef = useRef<number | null>(null);
  const deepsightsOpenRafRef = useRef<number | null>(null);

  // Attachments tab state
  const [attachments, setAttachments] = useState<SparkAttachment[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<SparkAttachment | null>(null);
  const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
  const attachmentsLoadedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);


  // Helper to pick icon based on MIME type
  const pickIconForMime = (mime?: string) => {
    if (!mime) return <DescriptionIcon fontSize="small" />;
    if (mime.startsWith('image/')) return <ImageIcon fontSize="small" />;
    if (mime === 'application/pdf') return <PictureAsPdfIcon fontSize="small" />;
    if (mime === 'application/msword' || mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
      return <WordFileIcon />;
    if (mime === 'application/vnd.ms-excel' || mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      return <ExcelFileIcon />;
    if (mime === 'application/vnd.ms-powerpoint' || mime === 'application/vnd.openxmlformats-officedocument.presentationml.presentation')
      return <PowerPointFileIcon />;
    return <DescriptionIcon fontSize="small" />;
  };

  // Load attachments (called when tab becomes active)
  const loadAttachments = useCallback(async () => {
    if (isLoadingAttachments || attachmentsLoadedRef.current) return;
    setIsLoadingAttachments(true);
    try {
      let items: SparkAttachment[];
      if (attachmentsBackend) {
        items = await attachmentsBackend.list();
      } else if (isItemMode) {
        items = await atlasClient.listLibraryItemAttachments(iteration.fqn);
      } else {
        items = await atlasClient.listCreativeAttachments(iteration._creative_fqn);
      }
      setAttachments(items || []);
      attachmentsLoadedRef.current = true;
    } catch (err) {
      console.error('Failed to load attachments:', err);
      toast.error('Failed to load attachments');
    } finally {
      setIsLoadingAttachments(false);
    }
  }, [attachmentsBackend, isItemMode, iteration.fqn, iteration._creative_fqn, isLoadingAttachments]);

  // Upload attachment files
  const handleAttachmentFilesSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploadingAttachment(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        try {
          let item: SparkAttachment;
          if (attachmentsBackend) {
            item = await attachmentsBackend.upload(f);
          } else if (isItemMode) {
            item = await atlasClient.uploadLibraryItemAttachment(iteration.fqn, f);
          } else {
            item = await atlasClient.uploadCreativeAttachment(iteration._creative_fqn, f);
          }
          setAttachments(prev => [...prev, item]);
        } catch (err: any) {
          const message = err?.response?.data?.error || (err instanceof Error ? err.message : 'Failed to upload');
          toast.error(`Failed to upload ${f.name}: ${message}`);
        }
      }
    } finally {
      setIsUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [attachmentsBackend, isItemMode, iteration.fqn, iteration._creative_fqn]);

  // Delete attachment
  const confirmDeleteAttachment = useCallback(async () => {
    if (!attachmentToDelete) return;
    setIsDeletingAttachment(true);
    try {
      if (attachmentsBackend) {
        await attachmentsBackend.remove(attachmentToDelete.file_id);
      } else if (isItemMode) {
        await atlasClient.deleteLibraryItemAttachment(iteration.fqn, attachmentToDelete.file_id);
      } else {
        await atlasClient.deleteCreativeAttachment(iteration._creative_fqn, attachmentToDelete.file_id);
      }
      setAttachments(prev => prev.filter(a => a.file_id !== attachmentToDelete.file_id));
    } catch (err: any) {
      const message = err?.response?.data?.error || (err instanceof Error ? err.message : 'Failed to delete');
      toast.error(`Failed to delete: ${message}`);
    } finally {
      setIsDeletingAttachment(false);
      setAttachmentToDelete(null);
    }
  }, [attachmentsBackend, isItemMode, attachmentToDelete, iteration.fqn, iteration._creative_fqn]);

  // Get download URL for attachment
  const getAttachmentDownloadUrl = useCallback((fileId: string) => {
    if (attachmentsBackend) {
      return attachmentsBackend.getDownloadUrl(fileId);
    } else if (isItemMode) {
      return atlasClient.getLibraryItemAttachmentDownloadUrl(iteration.fqn, fileId);
    } else {
      return atlasClient.getCreativeAttachmentDownloadUrl(iteration._creative_fqn, fileId);
    }
  }, [attachmentsBackend, isItemMode, iteration.fqn, iteration._creative_fqn]);

  // Lazy load attachments when tab becomes active
  useEffect(() => {
    if (activeTab !== 'deepsights' && !attachmentsLoadedRef.current && !isLoadingAttachments) {
      loadAttachments();
    }
  }, [activeTab, loadAttachments, isLoadingAttachments]);

  useEffect(() => {
    if (deepsightsCloseTimerRef.current != null) {
      window.clearTimeout(deepsightsCloseTimerRef.current);
      deepsightsCloseTimerRef.current = null;
    }
    if (deepsightsOpenRafRef.current != null) {
      window.cancelAnimationFrame(deepsightsOpenRafRef.current);
      deepsightsOpenRafRef.current = null;
    }

    if (isDeepsightsOpen) {
      setIsDeepsightsSheetMounted(true);
      setIsDeepsightsSheetVisible(false);
      setIsDeepsightsExpanded(false);
      deepsightsOpenRafRef.current = window.requestAnimationFrame(() => {
        deepsightsOpenRafRef.current = window.requestAnimationFrame(() => {
          setIsDeepsightsSheetVisible(true);
          deepsightsOpenRafRef.current = null;
        });
      });
      return;
    }

    if (!isDeepsightsSheetMounted) {
      return;
    }

    setIsDeepsightsSheetVisible(false);
    deepsightsCloseTimerRef.current = window.setTimeout(() => {
      setIsDeepsightsSheetMounted(false);
      setIsDeepsightsExpanded(false);
      deepsightsCloseTimerRef.current = null;
    }, DEEPSIGHTS_SHEET_TRANSITION_MS);
  }, [isDeepsightsOpen, isDeepsightsSheetMounted]);

  useEffect(() => {
    return () => {
      if (deepsightsCloseTimerRef.current != null) {
        window.clearTimeout(deepsightsCloseTimerRef.current);
      }
      if (deepsightsOpenRafRef.current != null) {
        window.cancelAnimationFrame(deepsightsOpenRafRef.current);
      }
    };
  }, []);

  // Reset attachments loaded state when creative/item changes
  useEffect(() => {
    attachmentsLoadedRef.current = false;
    setAttachments([]);
  }, [attachmentContextFqn]);

  // State for the new combined input area
  const [newInputContent, setNewInputContent] = useState('');
  const [isAgentRunning, setIsAgentRunning] = useState(false); // To track chat API execution
  const [optimisticFeedback, setOptimisticFeedback] = useState<SparkFeedbackItem | null>(null);

  // ADDED: State for available labels for the project
  const [availableLabels, setAvailableLabels] = useState<string[]>([]);

  // Description removed from profile page

  // State for slide viewer functionality
  const [supportedFormats, setSupportedFormats] = useState<string[]>([]);
  const [isLoadingFormats, setIsLoadingFormats] = useState(true);
  const [isGeneratingSlides, setIsGeneratingSlides] = useState(false);
  const [isSlideLightboxOpen, setIsSlideLightboxOpen] = useState(false);
  const [slideViewData, setSlideViewData] = useState<{
    exportId: string;
    fileCount: number;
    iterationFqn: string;
  } | null>(null);
  const [isDownloadingPptx, setIsDownloadingPptx] = useState(false);
  const [slideCountdown, setSlideCountdown] = useState<number | null>(null);

  // Refs to track recent export requests and prevent duplicates in React dev mode
  const lastSlideExportTimeRef = useRef<number>(0);
  const lastPptxExportTimeRef = useRef<number>(0);
  const EXPORT_DEBOUNCE_MS = 2000; // 2 second debounce window

  // Countdown timer for slide generation
  useEffect(() => {
    if (isGeneratingSlides) {
      setSlideCountdown(30);
      const interval = setInterval(() => {
        setSlideCountdown(prev => {
          if (prev === null || prev <= 0) return 0;
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setSlideCountdown(null);
    }
  }, [isGeneratingSlides]);

  // State to show thinking indicator
  const [showThinkingIndicator, setShowThinkingIndicator] = useState(false);

  // SSE streaming state for iteration chat
  const [expandedThinkingPanels, setExpandedThinkingPanels] = useState<Set<string>>(new Set());
  const [isLiveThinkingExpanded, setIsLiveThinkingExpanded] = useState(false);
  const isLiveThinkingExpandedRef = useRef(false);
  const [feedbackThinkingSteps, setFeedbackThinkingSteps] = useState<Map<string, ThinkingStep[]>>(new Map());

  const { sendMessage: sendSSEMessage, cancelRequest, thinkingSteps, clearThinkingSteps } = useIterationSSE({
    onError: (msg) => toast.error(msg),
  });

  // State to track whether to skip auto-scroll on next update


  const containerRef = useRef<HTMLDivElement>(null);
  const feedbackListRef = useRef<HTMLDivElement>(null);
  const deepsightsViewportRef = useRef<HTMLDivElement>(null);

  // Refs for editable inputs (none for description; removed)

  // Ref to track previous feedback length for auto-scroll
  const prevFeedbackLengthRef = useRef<number>(iteration._feedback.length);
  const prevIterationFqnRef = useRef<string>(iteration.fqn);

  // Throttled scroll to bottom of deepsights viewport - for typewriter auto-scroll
  const lastScrollTimeRef = useRef(0);
  const scrollDeepsightsToBottom = useCallback((smooth = true) => {
    // Use parent scroll container when provided, otherwise the viewport itself
    const viewport = scrollContainerRef?.current ?? deepsightsViewportRef.current;
    if (!viewport) return;

    // Throttle scrolls to max once per 50ms for smooth experience
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 50) return;
    lastScrollTimeRef.current = now;

    if (smooth) {
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [scrollContainerRef]);

  // Stable callback for typewriter scroll — use rAF to ensure DOM is painted
  // before measuring scrollHeight, and instant scroll to avoid smooth animation
  // lag with fast-updating content (matches briefing assistant pattern)
  const handleTypewriterProgress = useCallback(() => {
    requestAnimationFrame(() => {
      scrollDeepsightsToBottom(false);
    });
  }, [scrollDeepsightsToBottom]);

  // Auto-scroll when thinking steps update during SSE streaming
  useEffect(() => {
    if (thinkingSteps.length > 0) {
      requestAnimationFrame(() => scrollDeepsightsToBottom(true));
    }
  }, [thinkingSteps, scrollDeepsightsToBottom]);

  // Cleanup SSE request on unmount
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, [cancelRequest]);

  // Mark existing AI feedback items as already animated on initial load or iteration change
  // This prevents animating old messages when the component first mounts or when switching iterations
  const lastInitializedIterationRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastInitializedIterationRef.current !== iteration.fqn) {
      // First mount or iteration changed - mark all existing AI messages as animated
      const existingAiIds = iteration._feedback
        .filter(f => f._source === 'AI' || f._source === 'AGENT')
        .map(f => f._id);
      setAnimatedFeedbackIds(new Set(existingAiIds));
      lastInitializedIterationRef.current = iteration.fqn;
    }
  }, [iteration.fqn, iteration._feedback]);

  // Store actions
  const setSparkUserInteractionBlockingRefresh = useAtlasStore(
    (state: AtlasState) => state.setSparkUserInteractionBlockingRefresh
  );
  const formDefinitionsByCreative = useAtlasStore((state: AtlasState) => state.sparkFormDefinitions);
  const creativeTypes = useAtlasStore((state: AtlasState) => state.sparkCreativeTypes);
  const currentCreatives = useAtlasStore((state: AtlasState) => state.currentSparkCreatives);
  const currentSparkFlow = useAtlasStore((state: AtlasState) => state.currentSparkFlow);
  const validationIssuesByIteration = useAtlasStore(
    (state: AtlasState) => state.sparkIterationValidationIssues
  );
  const tenantUsers = useAtlasStore((state: AtlasState) => state.tenantUsers);
  const fetchTenantUsers = useAtlasStore((state: AtlasState) => state.fetchTenantUsers);
  const isLoadingTenantUsers = useAtlasStore((state: AtlasState) => state.isLoadingTenantUsers);
  const addSparkLabel = useAtlasStore((state: AtlasState) => state.addSparkLabel);

  // Dropdown for selecting or adding labels

  const creativeKey = useMemo(() => (typeof iteration._creative_fqn === 'string' ? iteration._creative_fqn : String(iteration._creative_fqn)), [iteration._creative_fqn]);
  const currentCreativeType = useMemo(() => creativeTypes.find((t: SparkCreativeType) => t.fqn === (typeof iteration._creative_type_fqn === 'string' ? iteration._creative_type_fqn : String(iteration._creative_type_fqn))), [creativeTypes, iteration._creative_type_fqn]);
  const currentCreative = useMemo(() => currentCreatives.find(c => (c as any).fqn === creativeKey), [currentCreatives, creativeKey]);
  const formsMap: Record<string, SparkFormDefinition> = useMemo(() => {
    if (formsMapOverride && Object.keys(formsMapOverride).length) {
      return formsMapOverride;
    }
    const defs = formDefinitionsByCreative[creativeKey] as any;
    return (defs as Record<string, SparkFormDefinition>) || {};
  }, [formsMapOverride, formDefinitionsByCreative, creativeKey]);

  const orderedFormIds: string[] = useMemo(() => {
    if (orderedFormIdsOverride && orderedFormIdsOverride.length) {
      return orderedFormIdsOverride;
    }
    const ids = Object.keys(formsMap || {});
    const creativeOverlay = (currentCreative as any)?.forms_overlay as { id: string }[] | undefined;
    if (creativeOverlay && creativeOverlay.length) {
      const order = creativeOverlay.map(f => f.id).filter(id => ids.includes(id));
      if (order.length) return order;
    }
    const typeOverlay = (currentCreativeType as any)?.forms_overlay as { id: string }[] | undefined;
    if (typeOverlay && typeOverlay.length) {
      const order = typeOverlay.map(f => f.id).filter(id => ids.includes(id));
      if (order.length) return order;
    }
    return ids;
  }, [orderedFormIdsOverride, formsMap, currentCreative, currentCreativeType]);

  const [localSelectedFormId, setLocalSelectedFormId] = useState<string>(orderedFormIds[0] || '');
  const selectedFormId = controlledSelectedFormId ?? localSelectedFormId;
  const setSelectedFormId = useCallback((fid: string) => {
    setLocalSelectedFormId(fid);
    onSelectedFormIdChange?.(fid);
  }, [onSelectedFormIdChange]);
  const formatFormLabel = useCallback((fid: string) => {
    return resolveSparkFormLabelFromOverlay(fid, currentCreative?.forms_overlay, currentCreativeType?.forms_overlay)
      ?? formatSparkFormLabel(fid, formsMap[fid]);
  }, [currentCreative?.forms_overlay, currentCreativeType?.forms_overlay, formsMap]);
  useEffect(() => {
    if (orderedFormIds.length && !orderedFormIds.includes(selectedFormId)) {
      setSelectedFormId(orderedFormIds[0]);
    }
  }, [orderedFormIds, selectedFormId, setSelectedFormId]);

  // Compute missing required fields across all forms (for publish gating)
  const hasMissingRequiredAcrossForms = useMemo(() => {
    try {
      const definitions = Object.values((formsMap || {})) as SparkFormDefinition[];
      if (!definitions || definitions.length === 0) return false;
      const flatValues = (iteration as any).values || {};
      const images = (iteration as any).images || {};
      for (const def of definitions) {
        for (const group of (def?.groups || [])) {
          for (const el of (group?.elements || [])) {
            if (!el.required) continue;
            const v = flatValues[el.id];
            const img = images[el.id];
            const empty =
              v === undefined || v === null ||
              (typeof v === 'string' && v.trim().length === 0) ||
              (Array.isArray(v) && v.length === 0) ||
              (el.type === 'image' && (!img || !(img.current_file_ids || []).length));
            if (empty) return true;
          }
        }
      }
      return false;
    } catch {
      return false;
    }
  }, [formsMap, iteration]);

  // Publish button gating and labels (computed after required scan)
  const publishActionDisabled = isPublishing || (isPublished ? !canUnpublish : !canPublish) || (!isPublished && hasMissingRequiredAcrossForms);
  const publishButtonLabel = isPublished ? 'Unpublish' : 'Publish';
  const publishButtonTitle = isPublished
    ? (closed ? 'Project is closed.' : 'Unpublish creative')
    : (hasMissingRequiredAcrossForms ? 'Complete all required fields to publish' : (publishDisabledReason || 'Publish creative'));

  const iterationValidationIssues = useMemo(() => {
    const byIter = validationIssuesByIteration[iteration.fqn] as any;
    if (byIter && selectedFormId && byIter[selectedFormId]) return byIter[selectedFormId];
    return [] as any[];
  }, [validationIssuesByIteration, iteration.fqn, selectedFormId]);

  // imageElementsById not used; removed to reduce noise

  const handleFormDirtyChange = useCallback(
    (dirty: boolean) => {
      setSparkUserInteractionBlockingRefresh(dirty);
    },
    [setSparkUserInteractionBlockingRefresh]
  );

  const labelDropdownRef = useRef<HTMLDivElement>(null);
  const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState(false);

  // State and Ref for Iteration Dropdown
  const [isIterationDropdownOpen, setIsIterationDropdownOpen] = useState(false);
  const iterationDropdownRef = useRef<HTMLDivElement>(null);
  const iterationBadgeTriggerRef = useRef<HTMLDivElement>(null);

  // ADDED: useEffect to fetch all labels for the project
  useEffect(() => {
    if (isItemMode) return;
    const fetchProjectLabels = async () => {
      if (!projectFqn) return;
      try {
        const fetchedLabels = await atlasClient.getSparkProjectLabels(projectFqn);
        setAvailableLabels(fetchedLabels.sort());
      } catch (error) {
        console.error('Error fetching project labels:', error);
        toast.error('Could not load project labels.');
        setAvailableLabels([]);
      }
    };
    fetchProjectLabels();
  }, [projectFqn, isItemMode]);

  // Check supported export formats when iteration changes
  useEffect(() => {
    if (isItemMode) {
      // Item mode: probe item export formats
      const checkItemFormats = async () => {
        if (!iteration.fqn) return;
        setIsLoadingFormats(true);
        try {
          const data = await authenticatedFetch(
            `/api/spark/library/items/${encodeURIComponent(iteration.fqn)}/export/formats`
          ).then(parseJsonResponse);
          setSupportedFormats((data?.supported_formats as string[]) || []);
        } catch (error) {
          console.error('Failed to check item export formats:', error);
          setSupportedFormats([]);
        } finally {
          setIsLoadingFormats(false);
        }
      };
      checkItemFormats();
      return;
    }
    const checkSupportedFormats = async () => {
      if (!iteration.fqn) return;
      setIsLoadingFormats(true);
      try {
        const response = await authenticatedFetch(
          `/api/spark/iterations/${encodeURIComponent(iteration.fqn)}/export/formats`
        ).then(parseJsonResponse);
        setSupportedFormats(response.supported_formats || []);
      } catch (error) {
        console.error('Failed to check supported formats:', error);
        setSupportedFormats([]);
      } finally {
        setIsLoadingFormats(false);
      }
    };
    checkSupportedFormats();
  }, [iteration.fqn, isItemMode]);

  // Lift presentation state to parent (when header is hidden)
  useEffect(() => {
    if (onPresentStateChange) {
      onPresentStateChange({
        showPresent: !isLoadingFormats && supportedFormats.includes('html'),
        isPresentActive: isSlideLightboxOpen,
        isGeneratingSlides,
        slideCountdown: slideCountdown ?? null,
        showDownloadPptx: isSlideLightboxOpen,
        isDownloadingPptx,
      });
    }
  }, [onPresentStateChange, isLoadingFormats, supportedFormats, isSlideLightboxOpen, isGeneratingSlides, slideCountdown, isDownloadingPptx]);

  // Fetch tenant users if not already loaded
  useEffect(() => {
    if (tenantUsers.length === 0 && !isLoadingTenantUsers) {
      fetchTenantUsers();
    }
  }, [tenantUsers, isLoadingTenantUsers, fetchTenantUsers]);

  useEffect(() => {
    handleFormDirtyChange(false);
  }, [handleFormDirtyChange, iteration]);

  // Description editing removed

  // Mark a feedback item as having completed its typewriter animation
  const markFeedbackAnimated = useCallback((feedbackId: string) => {
    setAnimatedFeedbackIds(prev => {
      const next = new Set(Array.from(prev));
      next.add(feedbackId);
      return next;
    });
  }, []);

  // Handle removing a label
  const handleRemoveLabel = (label: string) => {
    if (onRemoveLabel) {
      onRemoveLabel(label);
    }
  };

  // Handle adding a new label to this creative
  const handleAddLabel = async (e: React.FormEvent) => {
    e.preventDefault();
    const label = newLabelInput.trim();
    if (!label) return;
    try {
      await addSparkLabel([iteration._creative_fqn], label);
      // Optimistically update availableLabels state
      if (!availableLabels.includes(label)) {
        setAvailableLabels(prevLabels => [...prevLabels, label].sort());
      }
      if (onUpdate) onUpdate();
      setNewLabelInput('');
      setIsLabelDropdownOpen(false);
    } catch (error) {
      console.error('Failed to add label:', error);
      toast.error('Failed to add label. Please try again.');
    }
  };

  // Handle selecting an existing label
  const handleAddExistingLabel = async (label: string) => {
    try {
      await addSparkLabel([iteration._creative_fqn], label);

      if (onUpdate) onUpdate();
      setIsLabelDropdownOpen(false);
    } catch (error) {
      console.error('Failed to add label:', error);
      toast.error('Failed to add label. Please try again.');
    }
  };

  // Helper function to get user display name
  const getUserDisplayName = (fqn: string): string => {
    const user = tenantUsers.find(u => u.fqn === fqn);
    // Fallback to FQN if user not found or name is missing
    return user?.name || fqn;
  };

  // Helper function to format action names nicely
  // formatActionName unused

  // Check if feedback is from AI
  const isSystemOrAIFeedback = (feedbackItem: SparkFeedbackItem): boolean => {
    return feedbackItem._source === 'AI' || feedbackItem._source === 'AGENT';
  };

  const isEvaluationFeedback = (feedbackItem: SparkFeedbackItem): boolean => {
    return feedbackItem._source === 'AGENT' && feedbackItem._metadata?.result_kind === 'evaluation';
  };

  const getEvaluationFeedbackPresentation = (feedbackItem: SparkFeedbackItem): { logoUrl?: string; sourceName?: string } => {
    const agentName = feedbackItem._metadata?.agent_name;
    if (!agentName || !currentSparkFlow?.actions?.length) {
      return {};
    }

    const action = currentSparkFlow.actions.find((candidate) => candidate.name.toLowerCase() === String(agentName).toLowerCase());
    if (!action?.logo_url) {
      return {};
    }

    return {
      logoUrl: action.logo_url,
      sourceName: action.label.replace(/\s+Evaluator$/i, '').trim() || action.label,
    };
  };

  const renderAuthorInfo = (feedbackItem: SparkFeedbackItem) => {
    if (isSystemOrAIFeedback(feedbackItem)) {
      return (
        <span className={styles.aiUsername}>
          <AutoAwesomeIcon className={styles.aiAuthorIcon} />
          DeepSights
        </span>
      );
    } else {
      return <span className={styles.userUsername}>{getUserDisplayName(feedbackItem._user_fqn)}</span>;
    }
  };

  // Format timestamp for display (relative time)
  const formatTimestamp = (timestamp: string): string => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) { // Approximation for weeks in a month
      return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30); // Approximation
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    }

    const diffInYears = Math.floor(diffInDays / 365); // Approximation
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  };

  // Function to update the last time seen timestamp
  const updateLastTimeSeen = useCallback(async (refresh: boolean = true) => {
    if (isItemMode) return; // library items do not support mark-as-seen
    try {
      await authenticatedFetch(
        `/api/spark/iterations/${encodeURIComponent(iteration.fqn)}/mark_as_seen`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      ).then(parseJsonResponse);
    } catch (err) {
      console.error('Failed to update last time seen:', err);
    }
  }, [iteration.fqn, isItemMode]);

  // Function to mark ALL iterations in this creative as seen
  const markAllIterationsAsSeen = useCallback(async () => {
    if (isItemMode) return; // not applicable to items
    try {
      // Mark all iterations as seen by calling the API for each one
      await Promise.all(
        iterations.map(iter =>
          authenticatedFetch(
            `/api/spark/iterations/${encodeURIComponent(iter.fqn)}/mark_as_seen`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ).then(parseJsonResponse)
        )
      );
    } catch (err) {
      console.error('Failed to mark all iterations as seen:', err);
    }
  }, [iterations, isItemMode]);

  // Function to find and scroll to the first unseen feedback item
  const scrollToFirstUnseenFeedback = useCallback(() => {
    if (!feedbackListRef.current || !iteration._feedback.length) return;

    // Get the last time the iteration was seen by the current user
    const lastTimeSeen = iteration._last_time_seen ? new Date(iteration._last_time_seen).getTime() : 0;

    // Find the index of the first unseen feedback item
    let unseenItemIndex = -1;
    for (let i = 0; i < iteration._feedback.length; i++) {
      const feedbackTime = new Date(iteration._feedback[i]._timestamp).getTime();
      if (feedbackTime > lastTimeSeen) {
        unseenItemIndex = i;
        break;
      }
    }

    // If there's an unseen item, scroll to it
    if (unseenItemIndex !== -1 && feedbackListRef.current) {
      const feedbackItems = feedbackListRef.current.querySelectorAll(`.${styles.feedbackItem}`);
      const target = feedbackItems[unseenItemIndex] as HTMLElement | undefined;
      const viewport = scrollContainerRef?.current ?? deepsightsViewportRef.current;
      if (target && viewport) {
        // Scroll the deepsights viewport so that the target item aligns near the top
        const viewportTop = viewport.getBoundingClientRect().top;
        const targetTop = target.getBoundingClientRect().top;
        const delta = targetTop - viewportTop;
        viewport.scrollTop += delta;
      }
    }
  }, [iteration._feedback, iteration._last_time_seen, scrollContainerRef]);

  const alignFeedbackTargetToViewport = useCallback((target: HTMLElement | null): boolean => {
    const viewport = scrollContainerRef?.current ?? deepsightsViewportRef.current;
    if (!target || !viewport) {
      return false;
    }

    const viewportTop = viewport.getBoundingClientRect().top;
    const targetTop = target.getBoundingClientRect().top;
    const delta = targetTop - viewportTop - 12;
    viewport.scrollTop = Math.max(0, viewport.scrollTop + delta);
    return true;
  }, [scrollContainerRef]);

  // Scroll to first unseen feedback on initial mount or iteration change only
  const prevUnseenIterFqnRef = useRef<string | null>(null);
  useEffect(() => {
    if (isItemMode) return; // do not mark-seen for items
    // Only act on initial mount or actual iteration change, not _last_time_seen refreshes
    if (prevUnseenIterFqnRef.current === iteration.fqn) return;
    prevUnseenIterFqnRef.current = iteration.fqn;

    // If this iteration was never seen by the user, mark as seen
    if (!iteration._last_time_seen) {
      updateLastTimeSeen(false);
    }
    // Schedule scroll to the first unseen feedback
    setTimeout(scrollToFirstUnseenFeedback, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iteration.fqn, iteration._last_time_seen, updateLastTimeSeen, scrollToFirstUnseenFeedback, isItemMode]);

  // Effect to scroll to bottom when new feedback is added
  useEffect(() => {
    const currentLength = iteration._feedback.length;
    const hasOptimistic = optimisticFeedback !== null;

    const prevFqn = prevIterationFqnRef.current;
    const iterationChanged = prevFqn !== iteration.fqn;

    // If the user switched iterations, do not auto-scroll to bottom.
    // Instead, reset our baseline counts and remember the new iteration.
    if (iterationChanged) {
      // When switching iterations, keep the view at the top of the panel (details tab).
      if (activeTab !== 'deepsights' && containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
      prevFeedbackLengthRef.current = currentLength;
      prevIterationFqnRef.current = iteration.fqn;
      setLocallySeenAt(null);
      return;
    }

    // Only auto-scroll when new feedback is appended on the same iteration
    if (activeTab === 'deepsights' && (currentLength > prevFeedbackLengthRef.current || hasOptimistic)) {
      // Instant scroll to bottom for new messages (matches briefing assistant pattern)
      scrollDeepsightsToBottom(false);
      // Mark as seen if new feedback arrived while viewing this iteration
      setLocallySeenAt(Date.now());
      // Fire-and-forget server update without forcing a full refresh
      updateLastTimeSeen(false);
    }

    prevFeedbackLengthRef.current = currentLength;
  }, [iteration.fqn, iteration._feedback, optimisticFeedback, activeTab, updateLastTimeSeen, scrollContainerRef, scrollDeepsightsToBottom]);

  // Effect to scroll to a specific feedback item when scrollToFeedbackTimestamp is set (controlled by parent)
  useEffect(() => {
    if (!scrollToFeedbackTimestamp || activeTab !== 'deepsights') return;

    // Wait for DOM to update
    requestAnimationFrame(() => {
      const feedbackItems = feedbackListRef.current?.querySelectorAll(`.${styles.feedbackItem}`);
      if (!feedbackItems) return;

      // Find item with matching timestamp
      for (let i = 0; i < iteration._feedback.length; i++) {
        if (iteration._feedback[i]._timestamp === scrollToFeedbackTimestamp) {
          const target = feedbackItems[i] as HTMLElement;
          alignFeedbackTargetToViewport(target);
          onScrollComplete?.();
          break;
        }
      }
    });
  }, [alignFeedbackTargetToViewport, scrollToFeedbackTimestamp, activeTab, iteration._feedback, onScrollComplete]);

  useEffect(() => {
    if (!scrollToFeedbackId || activeTab !== 'deepsights') return;

    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled || !feedbackListRef.current) {
        return;
      }

      const evaluationCard = feedbackListRef.current.querySelector<HTMLElement>(
        `[data-evaluation-feedback-id="${scrollToFeedbackId}"]`
      );
      const feedbackItem = feedbackListRef.current.querySelector<HTMLElement>(
        `[data-feedback-id="${scrollToFeedbackId}"]`
      );

      if (alignFeedbackTargetToViewport(evaluationCard || feedbackItem)) {
        onScrollComplete?.();
        return;
      }

      if (attempts >= 4) {
        return;
      }

      attempts += 1;
      window.requestAnimationFrame(tryScroll);
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(tryScroll);
    });

    return () => {
      cancelled = true;
    };
  }, [alignFeedbackTargetToViewport, scrollToFeedbackId, activeTab, iteration._feedback, onScrollComplete]);

  // Prepare DeepSights action dropdown
  const { flow } = useSparkProjectData(projectFqn);
  const creative = useAtlasStore((state: AtlasState) =>
    state.currentSparkCreatives.find((c: SparkCreative) => c.fqn === iteration._creative_fqn)
  );
  const stepIndex = flow?.steps.findIndex((s: SparkFlowStep) => s.creative_type_fqn === iteration._creative_type_fqn) ?? -1;
  const activeBoardStepIndex = stepIndex >= 0 ? 2 + stepIndex : -1;
  const selectedCreatives = creative ? [creative] : [];
  const { actions: applicableActions } = useSparkActions(
    flow,
    activeBoardStepIndex,
    selectedCreatives
  );

  // Actions to show as informational chips (all applicable actions)
  const availableActions = useMemo(() => {
    return applicableActions; // Show all applicable actions as informational chips
  }, [applicableActions]);

  const [currentAction, setCurrentAction] = useState<ResolvedSparkAction | null>(null);
  const [currentActionArgs, setCurrentActionArgs] = useState<Record<string, any>>({});
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);

  const handleActionSelect = (action: ResolvedSparkAction) => {
    if (isReadOnly) { informReadOnly(); return; }
    const baseArgs: Record<string, any> = {
      input_iteration_fqns: [iteration.fqn],
      input_creative_fqns: [iteration._creative_fqn],
    };
    if (action.output_type_fqn) baseArgs.output_type_fqn = action.output_type_fqn;
    setCurrentAction(action);
    setCurrentActionArgs(baseArgs);
    setIsActionModalOpen(true);
  };

  // Close label dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(event.target as Node)) {
        setIsLabelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close iteration dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside BOTH the trigger and the dropdown itself
      if (
        iterationDropdownRef.current &&
        !iterationDropdownRef.current.contains(event.target as Node) &&
        iterationBadgeTriggerRef.current &&
        !iterationBadgeTriggerRef.current.contains(event.target as Node)
      ) {
        setIsIterationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // Mark iteration as seen when leaving the view or switching iterations
  useEffect(() => {
    return () => {
      // On cleanup, mark the previous iteration as seen without refreshing
      updateLastTimeSeen(false);
    };
  }, [iteration.fqn, updateLastTimeSeen]);

  // Mark ALL iterations as seen when the IterationView component unmounts entirely
  // (i.e., when the creative editor closes)
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      // This cleanup runs only when the component unmounts entirely
      markAllIterationsAsSeen();
    };
  }, []); // Empty dependency array means this only runs on unmount
  /* eslint-enable react-hooks/exhaustive-deps */

  const displayedFeedbackItems = useMemo(() => {
    if (optimisticFeedback) {
      const alreadyPresent = iteration._feedback.some(
        (feedbackItem) => feedbackItem._id === optimisticFeedback._id
      );
      if (!alreadyPresent) {
        return [...iteration._feedback, optimisticFeedback];
      }
    }
    return iteration._feedback;
  }, [iteration._feedback, optimisticFeedback]);


  // Function to handle comment submission
  // Function to handle chat submission (submit button) - SSE streaming
  const handleSubmit = async () => {
    const content = newInputContent.trim();
    if (!content) return;

    setSparkUserInteractionBlockingRefresh(true);
    setIsAgentRunning(true);
    setShowThinkingIndicator(true);

    // Create optimistic user feedback
    const userFeedback: SparkFeedbackItem = {
      _id: `temp-${Date.now()}`, // Temporary ID
      _content: content,
      _timestamp: new Date().toISOString(),
      _user_fqn: currentUser?.id || 'unknown_user',
      _source: 'user' as const,
      _metadata: {},
      _job_status: ''
    };

    // Set optimistic feedback for immediate rendering
    setOptimisticFeedback(userFeedback);

    // Clear input immediately for better UX
    setNewInputContent('');

    // Preserve live thinking expanded state
    isLiveThinkingExpandedRef.current = false;
    setIsLiveThinkingExpanded(false);

    try {
      // SSE streaming call
      const result = await sendSSEMessage(iteration.fqn, content);

      if (result) {
        // Find the latest AI feedback ID from the returned iteration
        const returnedFeedback = result.iteration?._feedback;
        if (returnedFeedback && returnedFeedback.length > 0 && result.thinkingSteps.length > 0) {
          // Find the last AI feedback (the response we just got)
          // Backend source values are uppercase: "AI", "AGENT", "USER", "COMMENT"
          const lastAiFeedback = [...returnedFeedback].reverse().find(
            (f: any) => f._source === 'AI' || f._source === 'AGENT'
          );
          if (lastAiFeedback) {
            setFeedbackThinkingSteps((prev) => {
              const next = new Map(prev);
              next.set(lastAiFeedback._id, result.thinkingSteps);
              return next;
            });
            // Preserve expanded state from live thinking
            if (isLiveThinkingExpandedRef.current) {
              setExpandedThinkingPanels((prev) => {
                const next = new Set(prev);
                next.add(lastAiFeedback._id);
                return next;
              });
            }
          }
        }

        clearThinkingSteps();
      }

      // Always refresh to pick up backend state (user feedback + AI response are saved server-side)
      onUpdate?.();
    } catch (error: any) {
      console.error('Failed to submit chat:', error);
      toast.error(error.message || 'Failed to submit message.');
    } finally {
      setOptimisticFeedback(null);
      setShowThinkingIndicator(false);
      setIsAgentRunning(false);
      setSparkUserInteractionBlockingRefresh(false);
    }
  };

  // Handle keyboard events in the textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(); // Always trigger chat on Cmd/Ctrl+Enter
    }
  };

  // Handle toggling slide view
  const handleToggleSlideView = async () => {
    if (isSlideLightboxOpen) {
      // Close the lightbox
      setIsSlideLightboxOpen(false);
      return;
    }

    // Open lightbox - generate slides if needed
    if (!slideViewData) {
      // Debounce slide generation to prevent double calls in React dev mode
      const now = Date.now();
      if (now - lastSlideExportTimeRef.current < EXPORT_DEBOUNCE_MS) {
        console.log('Slide export debounced - too recent');
        return;
      }
      lastSlideExportTimeRef.current = now;

      setIsGeneratingSlides(true);
      try {
        const response = await authenticatedFetch(
          isItemMode
            ? `/api/spark/library/items/${encodeURIComponent(iteration.fqn)}/export?format=html`
            : `/api/spark/iterations/${encodeURIComponent(iteration.fqn)}/export?format=html`
        ).then(parseJsonResponse);

        setSlideViewData({
          exportId: response.export_id,
          fileCount: response.file_count,
          iterationFqn: iteration.fqn
        });
        setIsSlideLightboxOpen(true);
      } catch (error) {
        console.error('Failed to generate slides:', error);
        toast.error('Failed to generate slides. Please try again.');
      } finally {
        setIsGeneratingSlides(false);
      }
    } else {
      // Reuse existing slide data
      setIsSlideLightboxOpen(true);
    }
  };

  // Handle downloading PPTX when in slide view
  const handleDownloadPptx = async () => {
    // Prevent multiple simultaneous downloads
    if (isDownloadingPptx) return;

    // Debounce PPTX export to prevent double calls in React dev mode
    const now = Date.now();
    if (now - lastPptxExportTimeRef.current < EXPORT_DEBOUNCE_MS) {
      console.log('PPTX export debounced - too recent');
      return;
    }
    lastPptxExportTimeRef.current = now;

    setIsDownloadingPptx(true);
    try {
      const response = await authenticatedFetch(
        isItemMode
          ? `/api/spark/library/items/${encodeURIComponent(iteration.fqn)}/export?format=pptx`
          : `/api/spark/iterations/${encodeURIComponent(iteration.fqn)}/export?format=pptx`
      );

      // Create blob and download
      const blob = await response.blob();

      // Create URL and link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Manually construct filename
      const downloadFilename = `${iteration.name || (isItemMode ? 'item' : 'iteration')}.pptx`;
      link.setAttribute('download', downloadFilename);

      // Trigger download
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PPTX:', error);
      toast.error('Failed to download PPTX');
    } finally {
      setIsDownloadingPptx(false);
    }
  };

  // Expose presentation handlers to parent via ref (assign during render to always have latest closures)
  if (presentHandlersRef) {
    presentHandlersRef.current = {
      togglePresent: handleToggleSlideView,
      downloadPptx: handleDownloadPptx,
    };
  }

  // Refresh citation index when feedback count changes (new feedback may introduce new refs)
  useEffect(() => {
    refreshCitationIndex();
  }, [iteration._feedback.length, optimisticFeedback, refreshCitationIndex]);

  const handleExitDeepsights = useCallback(() => {
    onExitDeepsights?.();
  }, [onExitDeepsights]);

  const deepsightsOverlay = (() => {
    if (!isDeepsightsSheetMounted) {
      return null;
    }

    const overlay = (
      <div className={`${styles.chatSheetLayer} ${isDeepsightsSheetVisible ? styles.chatSheetLayerVisible : ''}`}>
        <button
          type="button"
          className={`${styles.chatSheetScrim} ${isDeepsightsSheetVisible ? styles.chatSheetScrimVisible : ''}`}
          aria-label="Minimize AI Actions"
          onClick={handleExitDeepsights}
        />
        <div
          className={`${styles.chatSheet} ${isDeepsightsSheetVisible ? styles.chatSheetVisible : ''} ${isDeepsightsExpanded ? styles.chatSheetExpanded : ''}`}
        >
          <div className={styles.chatSheetHeaderBar}>
            <div className={styles.chatSheetTitle}>
              <AutoAwesomeIcon className={styles.chatSheetTitleIcon} />
              <span>Explore</span>
            </div>
            <div className={styles.chatSheetHeaderActions}>
              <button
                type="button"
                className={styles.chatSheetIconButton}
                aria-label={isDeepsightsExpanded ? 'Restore AI Actions size' : 'Expand AI Actions'}
                onClick={() => setIsDeepsightsExpanded((prev) => !prev)}
              >
                <OpenInFullIcon fontSize="small" />
              </button>
              <button
                type="button"
                className={styles.chatSheetIconButton}
                aria-label="Minimize AI Actions"
                onClick={handleExitDeepsights}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </button>
            </div>
          </div>
          <div className={styles.chatSheetSurface}>
            <div className={styles.deepSightsCard}>
              <div className={styles.deepsightsViewport} ref={deepsightsViewportRef}>
                <div className={styles.feedbackList} ref={feedbackListRef}>
                  {displayedFeedbackItems.length === 0 ? (
                    <div className={styles.emptyFeedback}></div>
                  ) : (
                    displayedFeedbackItems.map((feedbackItem) => {
                      const isSystem = isSystemOrAIFeedback(feedbackItem);
                      const isEvaluation = isEvaluationFeedback(feedbackItem);
                      const evaluationPresentation = isEvaluation ? getEvaluationFeedbackPresentation(feedbackItem) : {};
                      const headerContent = (
                        <div className={styles.commentMeta}>
                          {renderAuthorInfo(feedbackItem)}
                          <span className={styles.aiTimestamp}>{formatTimestamp(feedbackItem._timestamp)}</span>
                        </div>
                      );

                      const contentBlock = (
                        <div className={styles.commentContent}>
                          {isEvaluation ? (
                            <EvaluationFeedbackCard
                              feedback={feedbackItem}
                              feedbackId={feedbackItem._id}
                              logoUrl={evaluationPresentation.logoUrl}
                              sourceName={evaluationPresentation.sourceName}
                            >
                              <ReactMarkdown components={markdownComponents} remarkPlugins={markdownPlugins}>
                                {normalizeMarkdownBullets(feedbackItem._content)}
                              </ReactMarkdown>
                            </EvaluationFeedbackCard>
                          ) : isSystem ? (
                            <TypewriterFeedbackContent
                              content={feedbackItem._content}
                              isAnimated={animatedFeedbackIds.has(feedbackItem._id)}
                              onAnimationComplete={() => markFeedbackAnimated(feedbackItem._id)}
                              onTyping={handleTypewriterProgress}
                              markdownComponents={markdownComponents}
                              markdownPlugins={markdownPlugins}
                            />
                          ) : (
                            <ReactMarkdown components={markdownComponents} remarkPlugins={markdownPlugins}>
                              {normalizeMarkdownBullets(feedbackItem._content)}
                            </ReactMarkdown>
                          )}
                        </div>
                      );

                      if (!isSystem) {
                        return (
                          <div key={feedbackItem._id} className={styles.userFeedbackWrapper} data-feedback-id={feedbackItem._id}>
                            <div className={styles.userFeedbackHeader}>
                              {headerContent}
                            </div>
                            <div className={`${styles.feedbackItem} ${styles.userFeedbackItem}`}>
                              {contentBlock}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={feedbackItem._id}
                          className={`${styles.feedbackItem} ${styles.aiSystemItem}`}
                          data-feedback-id={feedbackItem._id}
                        >
                          <div className={styles.feedbackItemHeader}>
                            {headerContent}
                          </div>
                          {(feedbackThinkingSteps.get(feedbackItem._id)?.length ?? 0) > 0 && (
                            <div className={styles.messageThinkingPanel}>
                              <ThinkingPanel
                                steps={feedbackThinkingSteps.get(feedbackItem._id)!}
                                isExpanded={expandedThinkingPanels.has(feedbackItem._id)}
                                sparkFlow={currentSparkFlow}
                                onToggle={() => {
                                  const wasExpanded = expandedThinkingPanels.has(feedbackItem._id);
                                  setExpandedThinkingPanels((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(feedbackItem._id)) next.delete(feedbackItem._id);
                                    else next.add(feedbackItem._id);
                                    return next;
                                  });
                                  if (!wasExpanded) {
                                    requestAnimationFrame(() => scrollDeepsightsToBottom(true));
                                  }
                                }}
                              />
                            </div>
                          )}
                          {contentBlock}
                        </div>
                      );
                    })
                  )}
                </div>
                {thinkingSteps.length > 0 && (
                  <div className={styles.liveThinkingPanel}>
                    <ThinkingPanel
                      steps={thinkingSteps}
                      isExpanded={isLiveThinkingExpanded}
                      sparkFlow={currentSparkFlow}
                      onToggle={() => {
                        setIsLiveThinkingExpanded((prev) => {
                          const next = !prev;
                          isLiveThinkingExpandedRef.current = next;
                          if (next) {
                            requestAnimationFrame(() => scrollDeepsightsToBottom(true));
                          }
                          return next;
                        });
                      }}
                    />
                  </div>
                )}
                {showThinkingIndicator && (thinkingSteps.length === 0 || thinkingSteps.every(s => s.status === 'completed')) && (
                  <div className={styles.thinkingIndicatorContainer}>
                    <div className={styles.thinkingIndicator}>
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
              </div>
              {!isReadOnly && (
                <div className={styles.combinedInputArea}>
                  <div className={styles.inputContainer}>
                    <TextareaAutosize
                      className={styles.feedbackTextarea}
                      value={newInputContent}
                      onChange={(e) => setNewInputContent(e.target.value)}
                      placeholder="Type your message..."
                      minRows={1}
                      maxRows={6}
                      disabled={isAgentRunning || isReadOnly}
                      onKeyDown={handleKeyDown}
                    />
                    <div className={styles.inputFooter}>
                      <div className={styles.agentChipsContainer}>
                        {availableActions.map((action) => (
                          <button
                            key={action.name}
                            type="button"
                            className={styles.agentChip}
                            title={action.formatted_description || action.formatted_label}
                            onClick={() => {
                              if (isReadOnly) {
                                informReadOnly();
                                return;
                              }
                              handleActionSelect(action);
                            }}
                          >
                            {action.logo_url ? (
                              <img src={action.logo_url} alt="" aria-hidden="true" className={styles.agentChipLogo} />
                            ) : (
                              <AutoAwesomeIcon className={styles.agentChipIcon} />
                            )}
                            <span className={styles.agentChipLabel}>{action.formatted_label}</span>
                          </button>
                        ))}
                      </div>
                      <div className={styles.inputButtons}>
                        <button
                          className={styles.submitButton}
                          onClick={() => {
                            if (isReadOnly) {
                              informReadOnly();
                              return;
                            }
                            handleSubmit();
                          }}
                          disabled={isReadOnly || !newInputContent.trim() || isAgentRunning}
                          title="Send message to chat"
                        >
                          <SendIcon />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );

    if (chatSheetHostRef?.current) {
      return createPortal(overlay, chatSheetHostRef.current);
    }

    return overlay;
  })();

  return (
    <div
      /* className={`${styles.container} ${commentsPanelMode === 'minimized' ? styles.containerMinimized : ''}`} */ /* Incorrect - container style shouldn't change */
      className={styles.container}
      ref={containerRef}
    >
      {/* Profile Panel */}
      <div
        className={styles.profilePanel}
      // Removed conditional style based on commentsPanelMode
      // style={
      //   commentsPanelMode === 'expanded'
      //     : undefined
      // }
      >
        {/* Height is now controlled purely by flexbox and CSS states */}
        {!hideHeader && (
        <div className={styles.profileHeader}>
          <div className={styles.profileHeaderContent}>
            {/* Title is displayed in the parent editor header; keep header right-side controls here */}

            {/* Edit profile actions - MOVED FROM HERE */}

            {/* Header right: version/labels/tags */}
            <div className={styles.headerRight}>
            {/* Container for badges (Labels + Jobs) */}
            {!isItemMode && (
            <div className={styles.badgesArea}>
              {/* Iteration badge cluster and anchored dropdown */}
              <div ref={iterationBadgeTriggerRef} style={{ position: 'relative' }}> {/* Wrapper for dropdown trigger */}
                {iterationNumber !== undefined && (
                  <div
                    className={styles.iterationBadgeContainer}
                    onClick={() => setIsIterationDropdownOpen(open => !open)} // Toggle dropdown on click
                    style={{ cursor: 'pointer' }} // Indicate clickability
                  >
                    {/* Dot on left when there are unseen changes */}
                    {displayedIterationHasUnseenChanges && <span className={styles.unseenDotInline} />}
                    <div className={isActiveIteration ? styles.iterationBadgeActive : styles.iterationBadgeInactive}>
                      {isActiveIteration ? (
                        <>
                          <CheckCircleIcon fontSize="small" style={{ fontSize: '14px' }} />
                          <span>v{iterationNumber}</span>
                        </>
                      ) : (
                        <span>v{iterationNumber}</span>
                      )}
                    </div>
                    {/* New iteration available action */}
                    {newIterationAvailable && onLoadNewIteration && (
                      <button
                        className={styles.newIterationButton}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent event from bubbling up
                          onLoadNewIteration(); // Call the original handler
                        }}
                        title="View new version"
                      >
                        New version available
                      </button>
                    )}
                  </div>
                )}

                {/* Iteration Dropdown Menu (anchored to trigger wrapper) */}
                {isIterationDropdownOpen && (
                  <div ref={iterationDropdownRef} className={styles.iterationDropdown}>
                    {/* Dropdown Header */}
                    <h4 className={styles.iterationDropdownHeader}>Versions</h4>
                    {/* Replicate iteration mapping from CreativeEditor */}
                    {iterations.map((iter, index) => {
                      const isActive = activeIterationFqn === iter.fqn;
                      const isDisplayed = displayedIterationFqn === iter.fqn;
                      const canDelete = !isActive && iterations.length > 1;
                      const baseUnseen = isIterationUnseen(iter);
                      const unseen = isDisplayed
                        ? (() => {
                          const lastSeenBase = parseTimestamp(iter._last_time_seen || null);
                          const effectiveLastSeen = Math.max(lastSeenBase, locallySeenAt ?? 0);
                          const changedAt = parseTimestamp(iter._changed_at || null);
                          if (!iter._last_time_seen && locallySeenAt == null) return true;
                          return changedAt > effectiveLastSeen;
                        })()
                        : baseUnseen;

                      return (
                        <div
                          key={iter.fqn}
                          className={styles.iterationDropdownItem}
                        >
                          {/* Dot for unseen changes */}
                          {unseen && <span className={styles.unseenDotTimeline} />}
                          {/* Iteration Badge (click to display) */}
                          <div
                            className={`${styles.iterationDropdownBadge} ${isDisplayed && isActive ? styles.displayedActiveIterationBadge :
                              isDisplayed ? styles.displayedBadge :
                                isActive ? styles.activeBadge :
                                  styles.inactiveBadge
                              }`}
                            title={isDisplayed ? `Viewing iteration ${iter._iteration_number}` : `View iteration ${iter._iteration_number}`}
                            onClick={() => handleDisplayIteration(iter.fqn)}
                          >
                            v{iter._iteration_number}
                          </div>
                          <div className={styles.iterationDropdownInfo}>
                            <div className={styles.iterationDropdownActions}>
                              {isActive ? (
                                <div className={styles.activeIndicator}>
                                  <CheckCircleIcon fontSize="small" style={{ color: '#4CAF50', marginRight: '4px' }} />
                                  <span>Active</span>
                                </div>
                              ) : (
                                !isReadOnly && (
                                  <button
                                    className={styles.setActiveButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetActiveIteration(iter.fqn);
                                    }}
                                    title={`Set v${iter._iteration_number} as active`}
                                  >
                                    Set Active
                                  </button>
                                )
                              )}
                              {!isReadOnly && (
                                <button
                                  className={styles.newIterationButtonDropdown}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCreateIteration(iter.fqn);
                                  }}
                                  title={`New iteration from v${iter._iteration_number}`}
                                >
                                  New
                                </button>
                              )}
                              {!isReadOnly && canDelete && (
                                <button
                                  className={styles.deleteButtonDropdown}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteIterationRequest(iter.fqn);
                                  }}
                                  title={`Delete v${iter._iteration_number}`}
                                  disabled={isDeletingIteration}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Job Count Badges - Placed next to labels */}
              <div className={styles.jobBadgesContainer}>
                {activeJobsCount > 0 && (
                  <div className={`${styles.jobBadge} ${styles.runningBadge}`}>
                    <AccessTimeIcon fontSize="inherit" className={styles.jobIcon} />
                    <span className={styles.jobText}> {activeJobsCount} running</span>
                  </div>
                )}
                {successJobsCount > 0 && (
                  <div className={`${styles.jobBadge} ${styles.successBadge}`}>
                    <CheckCircleOutlineIcon fontSize="inherit" className={styles.jobIcon} />
                    <span className={styles.jobText}> {successJobsCount} success</span>
                  </div>
                )}
                {failedJobsCount > 0 && (
                  <div className={`${styles.jobBadge} ${styles.failedBadge}`}>
                    <ErrorOutlineIcon fontSize="inherit" className={styles.jobIcon} />
                    <span className={styles.jobText}> {failedJobsCount} failed</span>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Labels + Manage Labels section */}
            <div className={styles.labelsPlusContainer} ref={labelDropdownRef}>
              <div className={styles.labelsContainer}>
                {creativeLabels.map(label => (
                  <div key={label} className={styles.labelChip}>
                    <LabelIcon fontSize="inherit" className={styles.labelIcon} />
                    <span className={styles.labelText}>{label}</span>
                    {onRemoveLabel && !isReadOnly && !isItemMode && (
                      <button
                        className={styles.labelRemoveButton}
                        onClick={() => handleRemoveLabel(label)}
                        title={`Remove ${label} label`}
                      >
                        <CloseIcon fontSize="inherit" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {!isReadOnly && !isItemMode && (
                <button
                  className={styles.addLabelActionButton}
                  onClick={() => setIsLabelDropdownOpen(open => !open)}
                  title="Manage labels"
                >
                  <AddIcon fontSize="small" />
                </button>
              )}

              {isLabelDropdownOpen && !isReadOnly && !isItemMode && (
                <div className={styles.labelDropdown}>
                  <h4 className={styles.iterationDropdownHeader}>Labels</h4>
                  <ul className={styles.labelsList}>
                    {availableLabels.map(label => (
                      <li
                        key={label}
                        className={styles.labelItem}
                        onClick={() => {
                          if (creativeLabels.includes(label)) {
                            handleRemoveLabel(label);
                          } else {
                            handleAddExistingLabel(label);
                          }
                        }}
                        title={
                          creativeLabels.includes(label)
                            ? `Remove label "${label}"`
                            : `Apply label "${label}"`
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={styles.labelManagementItem}>
                          <span className={styles.dropdownLabelText}>{label}</span>
                          <div className={styles.labelActions}>
                            {creativeLabels.includes(label) ? (
                              <DeleteIcon style={{ color: 'red', fontSize: '16px' }} />
                            ) : (
                              <CheckIcon fontSize="small" style={{ color: 'green' }} />
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <form className={styles.addLabelForm} onSubmit={handleAddLabel}>
                    <input
                      type="text"
                      className={styles.addLabelInput}
                      placeholder="Add new label..."
                      value={newLabelInput}
                      onChange={e => setNewLabelInput(e.target.value)}
                    />
                    <button
                      type="submit"
                      className={styles.addLabelButton}
                      disabled={!newLabelInput.trim()}
                    >
                      <AddIcon style={{ fontSize: '16px' }} />
                    </button>
                  </form>
                </div>
              )}
            </div>
            {/* Tag chips moved out to dedicated bar below header */}
            </div>

            {/* Creative type name moved to CreativeEditor modal header */}

            {/* ADDED: Header Actions (View Slides/Next/Close) */}
            <div className={styles.headerActions}>
              {/* Present toggle */}
              {!isLoadingFormats && supportedFormats.includes('html') && (
                <button
                  className={`${styles.publishToggleButton} ${styles.publishButton}`}
                  onClick={handleToggleSlideView}
                  disabled={isGeneratingSlides}
                  title={isSlideLightboxOpen ? "Close PowerPoint" : "View as slides"}
                >
                  {isGeneratingSlides ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SlideshowIcon fontSize="small" />
                  )}
                  {isGeneratingSlides ? (slideCountdown === 0 ? 'Any moment' : `~ ${slideCountdown} sec`) : 'PowerPoint'}
                </button>
              )}

              {/* Publish/Unpublish toggle (creatives) */}
              {!isItemMode && (canPublish || canUnpublish) && (
                <button
                  className={`${styles.publishToggleButton} ${isPublished ? styles.unpublishButton : styles.publishButton
                  }`}
                  onClick={handlePublishToggle}
                  disabled={publishActionDisabled}
                  title={publishButtonTitle}
                >
                  {isPublishing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    isPublished ? <UnpublishedIcon fontSize="small" /> : <PublishIcon fontSize="small" />
                  )}
                  {publishButtonLabel}
                </button>
              )}

              {/* Creative delete (visible unless published) */}
              {!isItemMode && !isPublished && onDeleteRequest && (
                <button
                  className={`${styles.publishToggleButton} ${styles.deleteButton}`}
                  onClick={onDeleteRequest}
                  title={'Delete creative'}
                >
                  <DeleteIcon style={{ width: 16, height: 16 }} />
                  Delete
                </button>
              )}

              {/* Item lifecycle controls */}
              {isItemMode && (
                <>
                  {!isPublished && canPublish && (
                    <button
                      className={`${styles.publishToggleButton} ${styles.publishButton}`}
                      onClick={() => { if (!hasMissingRequiredAcrossForms) onPublishRequest?.(); }}
                      title={(hasMissingRequiredAcrossForms ? 'Complete all required fields to publish' : (publishDisabledReason || 'Publish item'))}
                      disabled={hasMissingRequiredAcrossForms}
                    >
                      <PublishIcon fontSize="small" />
                      Publish
                    </button>
                  )}
                  {isPublished && canUnpublish && (
                    <button
                      className={`${styles.publishToggleButton} ${styles.unpublishButton}`}
                      onClick={onUnpublishRequest}
                      title={'Unpublish item'}
                    >
                      <UnpublishedIcon fontSize="small" />
                      Unpublish
                    </button>
                  )}
                  {canDelete && (
                    <button
                      className={`${styles.publishToggleButton} ${styles.deleteButton}`}
                      onClick={onDeleteRequest}
                      title={'Delete item'}
                    >
                      <DeleteIcon style={{ width: 16, height: 16 }} />
                      Delete
                    </button>
                  )}
                </>
              )}

              {/* Attachments hidden for now - TODO: tackle next */}

              {/* Download PPTX button removed - now in SlideLightbox */}

              {/* Next unseen */}
              {!isItemMode && hasNextUnseen && onNextUnseen && (
                <button
                  className={styles.nextUnseenButton}
                  onClick={onNextUnseen}
                  title="Go to next changed item"
                >
                  <SkipNextIcon fontSize="small" />
                </button>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Details toolbar removed; actions moved to header */}

        <div
          className={styles.profileContent}
        >
          {/* Description removed from profile page */}

          {/* Normal content (slides now shown in lightbox overlay) */}
            <div className={styles.contentWrapper}>
                  {orderedFormIds.map((fid) => {
                    const def = formsMap[fid] ?? null;
                    return (
                      <div
                        key={fid}
                        ref={(el) => { if (sectionRefs) sectionRefs.current[fid] = el; }}
                        data-section-id={fid}
                        className={editorStyles.viewerSection}
                      >
                        <div className={editorStyles.viewerSectionHeader}>
                          <h2 className={editorStyles.viewerSectionTitle}>{formatFormLabel(fid)}</h2>
                        </div>
                        <div className={styles.detailsFormContainer}>
                          <FormEditor
                            iteration={iteration}
                            formId={fid}
                            formDefinition={def}
                            validationIssues={iterationValidationIssues}
                            isReadOnly={isReadOnly}
                            onDirtyChange={handleFormDirtyChange}
                            onSubmitSuccess={() => {
                              onUpdate?.();
                              handleFormDirtyChange(false);
                            }}
                            markdownComponents={markdownComponents}
                            markdownPlugins={markdownPlugins}
                            projectFqn={projectFqn}
                            editorMode={editorMode}
                            persistValues={persistValues}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div
                    ref={(el) => { if (sectionRefs) sectionRefs.current['attachments'] = el; }}
                    data-section-id="attachments"
                    className={editorStyles.viewerSection}
                  >
                    <div className={editorStyles.viewerSectionHeader}>
                      <h2 className={editorStyles.viewerSectionTitle}>Attachments</h2>
                      {!isReadOnly && (
                        <button
                          className={editorStyles.viewerSectionAction}
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploadingAttachment}
                        >
                          {isUploadingAttachment ? 'Uploading...' : 'Add'}
                        </button>
                      )}
                    </div>
                    <div className={styles.attachmentsContent}>
                      {isLoadingAttachments ? (
                        <div className={styles.attachmentsLoading}>
                          <CircularProgress size={20} />
                          <span>Loading attachments...</span>
                        </div>
                      ) : attachments.length === 0 ? (
                        <div className={styles.attachmentsEmpty}>
                          <div className={styles.attachmentsEmptyIcon}>
                            <AttachFileIcon />
                          </div>
                          <h3 className={styles.attachmentsEmptyTitle}>No attachments yet</h3>
                        </div>
                      ) : (
                        <ul className={styles.attachmentsList}>
                          {attachments.map((att) => (
                            <li key={att.file_id} className={styles.attachmentItem}>
                              <a
                                href={getAttachmentDownloadUrl(att.file_id)}
                                download={att.filename}
                                title={`Download ${att.filename}`}
                                className={styles.attachmentLink}
                              >
                                <span className={styles.attachmentIcon}>
                                  {pickIconForMime(att.mime_type)}
                                </span>
                                <div className={styles.attachmentMeta}>
                                  <span className={styles.attachmentFilename} title={att.filename}>
                                    {att.filename}
                                  </span>
                                  <span className={styles.attachmentSize}>{formatBytes(att.size)}</span>
                                </div>
                              </a>
                              {!isReadOnly && (
                                <button
                                  className={styles.attachmentDeleteButton}
                                  onClick={() => setAttachmentToDelete(att)}
                                  title="Delete attachment"
                                >
                                  <DeleteIcon style={{ width: 16, height: 16 }} />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
                        style={{ display: 'none' }}
                        onChange={handleAttachmentFilesSelected}
                      />
                    </div>
                  </div>
            </div>
        </div>

      </div> { /* End of profilePanel */}
      {deepsightsOverlay}

      {/* Attachment Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!attachmentToDelete}
        onClose={() => setAttachmentToDelete(null)}
        onConfirm={confirmDeleteAttachment}
        title="Delete Attachment"
        message={attachmentToDelete ? `Are you sure you want to delete "${attachmentToDelete.filename}"? This action cannot be undone.` : ''}
        confirmText="Delete"
        variant="destructive"
        isConfirming={isDeletingAttachment}
      />

      {/* Spark Action Modal triggered from dropdown */}
      {currentAction && projectFqn && (
        <SparkActionModal
          isOpen={isActionModalOpen}
          onClose={() => setIsActionModalOpen(false)}
          title={currentAction.formatted_label}
          description={currentAction.formatted_description}
          actionName={currentAction.name}
          projectFqn={projectFqn}
          actionArgs={currentActionArgs}
          isGuidanceRequired={iteration._feedback.length === 0 && !iteration._rationale ? true : false && currentAction?.name === 'iterate'}
          onSuccess={() => setIsActionModalOpen(false)}
          resultKind={currentAction.result_kind}
          outputTypeName={currentAction.output_type_fqn
            ? flow?.creative_types.find((creativeType) => creativeType.fqn === currentAction.output_type_fqn)?.plural_name
              || flow?.creative_types.find((creativeType) => creativeType.fqn === currentAction.output_type_fqn)?.name
            : undefined}
          currentTypeName={creativeType?.name}
          isCurrentStep={
            currentAction.result_kind === 'creation' &&
            currentAction.output_type_fqn === iteration._creative_type_fqn
          }
        />
      )}

      {/* Render the Citation Portal for lightboxes/tooltips */}
      <CitationPortal />

      {/* Slide Lightbox */}
      {isSlideLightboxOpen && slideViewData && (
        <SlideLightbox
          exportId={slideViewData.exportId}
          iterationFqn={slideViewData.iterationFqn}
          fileCount={slideViewData.fileCount}
          onClose={() => setIsSlideLightboxOpen(false)}
          onDownloadPptx={handleDownloadPptx}
          isDownloadingPptx={isDownloadingPptx}
        />
      )}

    </div>
  );
};

export default IterationView; 
